import dagre from '@dagrejs/dagre';
import type { Edge, Node } from '@xyflow/react';
import { estimateNodeHeight } from './workflowNodeHelpers';
import { v4 as uuidv4 } from 'uuid';

const DAGRE_NODE_WIDTH = 300;
const SUBTREE_UNIT_WIDTH = 180;
const SUBTREE_SIBLING_GAP_UNITS = 1;
const DECISION_BRANCH_GAP_UNITS = 1;
const ROOT_GAP_PX = 220;
const EXIT_CENTER_WEIGHT = 0.5;
const DECISION_BRANCH_VERTICAL_GAP_PX = 56;

const buildDefaultExitNode = (): Node => ({
  id: uuidv4(),
  type: 'action',
  position: { x: 0, y: 0 },
  data: {
    label: 'Exit',
    status: 'configured',
    type: 'exit',
    isFixed: true,
    isDefault: true,
  },
  draggable: false,
  deletable: false,
});

const buildConditionMergeNode = (conditionId: string): Node => ({
  id: uuidv4(),
  type: 'action',
  position: { x: 0, y: 0 },
  data: {
    label: 'Merge',
    status: 'configured',
    type: 'merge',
    parentConditionId: conditionId,
    isFixed: true,
    isDefault: true,
  },
  draggable: false,
  deletable: false,
  selectable: false,
});

const toLockedExitNode = (node: Node): Node => ({
  ...node,
  type: 'action',
  draggable: false,
  deletable: false,
  data: {
    ...(node.data || {}),
    label: node.data?.label || 'Exit',
    status: 'configured',
    type: 'exit',
    isFixed: true,
    isDefault: true,
  },
});

const edgeKey = (edge: Edge) =>
  `${edge.source}::${edge.target}::${edge.sourceHandle || ''}::${edge.targetHandle || ''}`;

function sanitizeEdges(nodes: Node[], edges: Edge[]): Edge[] {
  const nodeIds = new Set(nodes.map((n) => n.id));
  const seen = new Set<string>();
  const out: Edge[] = [];

  for (const edge of edges) {
    if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) continue;
    if (edge.source === edge.target) continue;
    const key = edgeKey(edge);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...edge });
  }

  return out;
}

export function normalizeWorkflowGraph(
  nodes: Node[],
  edges: Edge[]
): { nodes: Node[]; edges: Edge[] } {
  let workingNodes = [...nodes];
  let workingEdges = [...edges];

  // Drop orphaned merge placeholders that do not belong to a condition anymore.
  const conditionNodeIds = new Set(
    workingNodes
      .filter((node) => node.data?.type === 'true-false')
      .map((node) => node.id)
  );
  workingNodes = workingNodes.filter((node) => {
    if (node.data?.type !== 'merge') return true;
    const parentConditionId = node.data?.parentConditionId as
      | string
      | undefined;
    return !!parentConditionId && conditionNodeIds.has(parentConditionId);
  });

  const exitNodes = workingNodes.filter((n) => n.data?.type === 'exit');
  const primaryExit = toLockedExitNode(exitNodes[0] || buildDefaultExitNode());
  const removedExitIds = new Set(exitNodes.slice(1).map((n) => n.id));

  workingNodes = workingNodes.filter((node) => {
    if (node.id === primaryExit.id) return true;
    return node.data?.type !== 'exit';
  });
  if (!workingNodes.some((n) => n.id === primaryExit.id)) {
    workingNodes.push(primaryExit);
  }

  workingEdges = workingEdges.filter(
    (edge) =>
      !removedExitIds.has(edge.source) &&
      !removedExitIds.has(edge.target) &&
      edge.source !== primaryExit.id
  );
  workingEdges = sanitizeEdges(workingNodes, workingEdges);

  const nextEdges: Edge[] = [...workingEdges];
  const outgoingBySource = new Map<string, Edge[]>();
  for (const edge of nextEdges) {
    const list = outgoingBySource.get(edge.source) || [];
    list.push(edge);
    outgoingBySource.set(edge.source, list);
  }

  // Enforce exactly one true-edge and one false-edge for condition nodes,
  // and ensure each condition has an owned merge placeholder.
  for (const node of workingNodes) {
    if (node.data?.type !== 'true-false') continue;

    let mergeNode = workingNodes.find(
      (candidate) =>
        candidate.data?.type === 'merge' &&
        candidate.data?.parentConditionId === node.id
    );
    if (!mergeNode) {
      mergeNode = buildConditionMergeNode(node.id);
      workingNodes.push(mergeNode);
    }

    const outgoing = outgoingBySource.get(node.id) || [];
    const trueEdge = outgoing.find((e) => e.sourceHandle === 'true');
    const falseEdge = outgoing.find((e) => e.sourceHandle === 'false');

    // Remove invalid extras for this condition source.
    for (let i = nextEdges.length - 1; i >= 0; i--) {
      const edge = nextEdges[i];
      if (edge.source !== node.id) continue;
      const isPrimaryTrue = trueEdge && edge.id === trueEdge.id;
      const isPrimaryFalse = falseEdge && edge.id === falseEdge.id;
      if (!isPrimaryTrue && !isPrimaryFalse) {
        nextEdges.splice(i, 1);
      }
    }

    if (!trueEdge) {
      nextEdges.push({
        id: uuidv4(),
        source: node.id,
        target: mergeNode.id,
        type: 'custom',
        sourceHandle: 'true',
        data: { branch: 'true' },
      });
    } else if (trueEdge.target === primaryExit.id) {
      const nextTrueEdge = nextEdges.find((edge) => edge.id === trueEdge.id);
      if (nextTrueEdge) {
        nextTrueEdge.target = mergeNode.id;
      }
    }
    if (!falseEdge) {
      nextEdges.push({
        id: uuidv4(),
        source: node.id,
        target: mergeNode.id,
        type: 'custom',
        sourceHandle: 'false',
        data: { branch: 'false' },
      });
    } else if (falseEdge.target === primaryExit.id) {
      const nextFalseEdge = nextEdges.find((edge) => edge.id === falseEdge.id);
      if (nextFalseEdge) {
        nextFalseEdge.target = mergeNode.id;
      }
    }
  }

  // Ensure all terminal nodes merge into the final flow (Exit).
  const terminalOutgoing = new Map<string, number>();
  for (const edge of nextEdges) {
    terminalOutgoing.set(
      edge.source,
      (terminalOutgoing.get(edge.source) || 0) + 1
    );
  }

  for (const node of workingNodes) {
    if (node.id === primaryExit.id) continue;
    const hasOutgoing = (terminalOutgoing.get(node.id) || 0) > 0;
    if (hasOutgoing) continue;
    nextEdges.push({
      id: uuidv4(),
      source: node.id,
      target: primaryExit.id,
      type: 'custom',
    });
  }

  const sanitized = sanitizeEdges(workingNodes, nextEdges);
  return {
    nodes: workingNodes,
    edges: assignMergeTargetHandles(workingNodes, sanitized),
  };
}

/** Dagre orders siblings unpredictably; true branch must be laid out left of false. */
function sortEdgesForDagre(nodes: Node[], edges: Edge[]): Edge[] {
  const typeById: Record<string, string | undefined> = {};
  for (const n of nodes) {
    typeById[n.id] = n.data?.type as string | undefined;
  }

  return [...edges].sort((a, b) => {
    if (a.source !== b.source) return a.source.localeCompare(b.source);
    const sourceIsBranch = typeById[a.source] === 'true-false';
    if (sourceIsBranch) {
      const order = (h: string | null | undefined) =>
        h === 'true' ? 0 : h === 'false' ? 1 : 2;
      const oa = order(a.sourceHandle);
      const ob = order(b.sourceHandle);
      if (oa !== ob) return oa - ob;
    }
    return a.target.localeCompare(b.target);
  });
}

/**
 * Every node id reachable from `rootId` via outgoing edges, including `rootId`.
 * Used for true/false deletion (both branches) and branch layout overlap checks.
 */
export function getReachableDescendantIds(
  rootId: string,
  edges: Edge[]
): Set<string> {
  const out = new Set<string>();
  const stack = [rootId];
  while (stack.length) {
    const id = stack.pop()!;
    if (out.has(id)) continue;
    out.add(id);
    for (const e of edges) {
      if (e.source === id) stack.push(e.target);
    }
  }
  return out;
}

type LayoutGraphContext = {
  nodeById: Map<string, Node>;
  outgoingBySource: Map<string, Edge[]>;
  incomingByTarget: Map<string, Edge[]>;
  primaryParentByNode: Map<string, string>;
  childrenByParent: Map<string, string[]>;
  subtreeUnitsByNode: Map<string, number>;
  xCenterByNode: Map<string, number>;
  nodeWidthById: Map<string, number>;
  previousCenterByNode: Map<string, number>;
};

export type WorkflowLayoutHint = {
  changedNodeIds?: string[];
  changedEdgeIds?: string[];
  operation?:
    | 'initialize'
    | 'node-add'
    | 'node-delete'
    | 'node-update'
    | 'edge-add'
    | 'edge-delete'
    | 'edge-update'
    | 'normalize';
};

const getNodeWidth = (node: Node): number => {
  if (typeof node.measured?.width === 'number' && node.measured.width > 0) {
    return node.measured.width;
  }
  return DAGRE_NODE_WIDTH;
};

function getBranchChildren(
  sourceId: string,
  outgoingBySource: Map<string, Edge[]>
): { trueChildId?: string; falseChildId?: string } {
  const outgoing = outgoingBySource.get(sourceId) || [];
  const trueEdge = outgoing.find((edge) => edge.sourceHandle === 'true');
  const falseEdge = outgoing.find((edge) => edge.sourceHandle === 'false');
  return {
    trueChildId: trueEdge?.target,
    falseChildId: falseEdge?.target,
  };
}

function alignImmediateDecisionChildrenTop(
  nodes: Node[],
  edges: Edge[]
): Node[] {
  const positions = new Map<string, { x: number; y: number }>();
  const nodeById = new Map<string, Node>();
  const outgoingBySource = new Map<string, Edge[]>();

  nodes.forEach((node) => {
    nodeById.set(node.id, node);
    positions.set(node.id, {
      x: node.position.x,
      y: node.position.y,
    });
  });

  edges.forEach((edge) => {
    const list = outgoingBySource.get(edge.source) || [];
    list.push(edge);
    outgoingBySource.set(edge.source, list);
  });

  for (const node of nodes) {
    if (node.data?.type !== 'true-false') continue;
    const outgoing = outgoingBySource.get(node.id) || [];
    const trueEdge = outgoing.find((edge) => edge.sourceHandle === 'true');
    const falseEdge = outgoing.find((edge) => edge.sourceHandle === 'false');
    if (!trueEdge || !falseEdge || trueEdge.target === falseEdge.target)
      continue;

    const truePos = positions.get(trueEdge.target);
    const falsePos = positions.get(falseEdge.target);
    if (!truePos || !falsePos) continue;

    const baselineY = Math.min(truePos.y, falsePos.y);
    const desiredChildTopY =
      node.position.y +
      estimateNodeHeight(node) +
      DECISION_BRANCH_VERTICAL_GAP_PX;
    const targetY = Math.min(baselineY, desiredChildTopY);
    const trueShift = targetY - truePos.y;
    const falseShift = targetY - falsePos.y;

    const trueDesc = getReachableDescendantIds(trueEdge.target, edges);
    const falseDesc = getReachableDescendantIds(falseEdge.target, edges);

    const exclusiveTrue = Array.from(trueDesc).filter(
      (id) => !falseDesc.has(id)
    );
    const exclusiveFalse = Array.from(falseDesc).filter(
      (id) => !trueDesc.has(id)
    );

    if (trueShift !== 0) {
      exclusiveTrue.forEach((id) => {
        const p = positions.get(id);
        if (p) p.y += trueShift;
      });
    }
    if (falseShift !== 0) {
      exclusiveFalse.forEach((id) => {
        const p = positions.get(id);
        if (p) p.y += falseShift;
      });
    }
  }

  return nodes.map((node) => {
    const nextPos = positions.get(node.id);
    return nextPos
      ? {
          ...node,
          position: nextPos,
        }
      : node;
  });
}

function createLayoutGraphContext(
  nodes: Node[],
  edges: Edge[]
): LayoutGraphContext {
  const nodeById = new Map<string, Node>();
  const outgoingBySource = new Map<string, Edge[]>();
  const incomingByTarget = new Map<string, Edge[]>();
  const previousCenterByNode = new Map<string, number>();
  const nodeWidthById = new Map<string, number>();

  for (const node of nodes) {
    nodeById.set(node.id, node);
    const width = getNodeWidth(node);
    nodeWidthById.set(node.id, width);
    previousCenterByNode.set(
      node.id,
      (typeof node.position?.x === 'number' ? node.position.x : 0) + width / 2
    );
  }

  for (const edge of edges) {
    const out = outgoingBySource.get(edge.source) || [];
    out.push(edge);
    outgoingBySource.set(edge.source, out);

    const incoming = incomingByTarget.get(edge.target) || [];
    incoming.push(edge);
    incomingByTarget.set(edge.target, incoming);
  }

  for (const [sourceId, list] of Array.from(outgoingBySource.entries())) {
    list.sort((a, b) => {
      const order = (h: string | null | undefined) =>
        h === 'true' ? 0 : h === 'false' ? 1 : 2;
      const oa = order(a.sourceHandle);
      const ob = order(b.sourceHandle);
      if (oa !== ob) return oa - ob;
      return a.target.localeCompare(b.target);
    });
    outgoingBySource.set(sourceId, list);
  }

  const primaryParentByNode = new Map<string, string>();
  for (const [targetId, incoming] of Array.from(incomingByTarget.entries())) {
    if (incoming.length === 0) continue;
    const sortedIncoming = [...incoming].sort((a, b) => {
      const ay = nodeById.get(a.source)?.position?.y ?? 0;
      const by = nodeById.get(b.source)?.position?.y ?? 0;
      if (ay !== by) return ay - by;
      const ah =
        a.sourceHandle === 'true' ? 0 : a.sourceHandle === 'false' ? 1 : 2;
      const bh =
        b.sourceHandle === 'true' ? 0 : b.sourceHandle === 'false' ? 1 : 2;
      if (ah !== bh) return ah - bh;
      return a.source.localeCompare(b.source);
    });
    primaryParentByNode.set(targetId, sortedIncoming[0].source);
  }

  const childrenByParent = new Map<string, string[]>();
  for (const [childId, parentId] of Array.from(primaryParentByNode.entries())) {
    const list = childrenByParent.get(parentId) || [];
    list.push(childId);
    childrenByParent.set(parentId, list);
  }

  for (const [parentId, children] of Array.from(childrenByParent.entries())) {
    const parent = nodeById.get(parentId);
    if (parent?.data?.type === 'true-false') {
      const { trueChildId, falseChildId } = getBranchChildren(
        parentId,
        outgoingBySource
      );
      const ordered = [trueChildId, falseChildId].filter(
        (id): id is string => !!id && children.includes(id)
      );
      const remaining = children
        .filter((id) => !ordered.includes(id))
        .sort((a, b) => a.localeCompare(b));
      childrenByParent.set(parentId, [...ordered, ...remaining]);
      continue;
    }

    const sorted = [...children].sort((a, b) => {
      const ax = previousCenterByNode.get(a) ?? 0;
      const bx = previousCenterByNode.get(b) ?? 0;
      if (ax !== bx) return ax - bx;
      return a.localeCompare(b);
    });
    childrenByParent.set(parentId, sorted);
  }

  return {
    nodeById,
    outgoingBySource,
    incomingByTarget,
    primaryParentByNode,
    childrenByParent,
    subtreeUnitsByNode: new Map<string, number>(),
    xCenterByNode: new Map<string, number>(),
    nodeWidthById,
    previousCenterByNode,
  };
}

function getSubtreeNodeIds(
  rootId: string,
  ctx: LayoutGraphContext
): Set<string> {
  const ids = new Set<string>();
  const stack = [rootId];
  while (stack.length) {
    const current = stack.pop()!;
    if (ids.has(current)) continue;
    ids.add(current);
    const children = ctx.childrenByParent.get(current) || [];
    children.forEach((childId) => stack.push(childId));
  }
  return ids;
}

function findRootId(nodeId: string, ctx: LayoutGraphContext): string {
  let current = nodeId;
  const visited = new Set<string>();
  while (ctx.primaryParentByNode.has(current) && !visited.has(current)) {
    visited.add(current);
    current = ctx.primaryParentByNode.get(current)!;
  }
  return current;
}

function deriveImpactedRootIds(
  roots: Node[],
  edges: Edge[],
  ctx: LayoutGraphContext,
  hint?: WorkflowLayoutHint
): Set<string> {
  if (!hint) {
    return new Set(roots.map((root) => root.id));
  }

  const impactedNodeIds = new Set<string>(hint.changedNodeIds || []);
  if (hint.changedEdgeIds?.length) {
    const edgeById = new Map<string, Edge>();
    for (const edge of edges) edgeById.set(edge.id, edge);
    for (const edgeId of hint.changedEdgeIds) {
      const edge = edgeById.get(edgeId);
      if (!edge) continue;
      impactedNodeIds.add(edge.source);
      impactedNodeIds.add(edge.target);
    }
  }

  if (impactedNodeIds.size === 0) {
    return new Set(roots.map((root) => root.id));
  }

  const impactedRoots = new Set<string>();
  impactedNodeIds.forEach((nodeId) => {
    if (!ctx.nodeById.has(nodeId)) return;
    impactedRoots.add(findRootId(nodeId, ctx));
  });
  return impactedRoots.size
    ? impactedRoots
    : new Set(roots.map((root) => root.id));
}

/** Recursive subtree width (in layout units) used for symmetric branch spacing. */
function computeSubtreeWidthUnits(
  nodeId: string,
  ctx: LayoutGraphContext
): number {
  const memoized = ctx.subtreeUnitsByNode.get(nodeId);
  if (typeof memoized === 'number') return memoized;

  const node = ctx.nodeById.get(nodeId);
  if (!node) return 1;

  const children = ctx.childrenByParent.get(nodeId) || [];

  let result = 1;
  if (node.data?.type === 'true-false') {
    const { trueChildId, falseChildId } = getBranchChildren(
      nodeId,
      ctx.outgoingBySource
    );
    const trueUnits = trueChildId
      ? computeSubtreeWidthUnits(trueChildId, ctx)
      : 1;
    const falseUnits = falseChildId
      ? computeSubtreeWidthUnits(falseChildId, ctx)
      : 1;
    result = trueUnits + falseUnits + DECISION_BRANCH_GAP_UNITS;
  } else if (children.length > 0) {
    const childUnits = children.reduce(
      (sum, childId) => sum + computeSubtreeWidthUnits(childId, ctx),
      0
    );
    const gapUnits =
      Math.max(0, children.length - 1) * SUBTREE_SIBLING_GAP_UNITS;
    result = Math.max(1, childUnits + gapUnits);
  }

  ctx.subtreeUnitsByNode.set(nodeId, result);
  return result;
}

function computeSubtreeWidths(
  nodeIds: string[],
  ctx: LayoutGraphContext
): void {
  nodeIds.forEach((nodeId) => {
    computeSubtreeWidthUnits(nodeId, ctx);
  });
}

function positionSubtree(
  nodeId: string,
  leftX: number,
  ctx: LayoutGraphContext
): void {
  const subtreeUnits = ctx.subtreeUnitsByNode.get(nodeId) || 1;
  const subtreeWidthPx = subtreeUnits * SUBTREE_UNIT_WIDTH;
  const nodeCenterX = leftX + subtreeWidthPx / 2;
  ctx.xCenterByNode.set(nodeId, nodeCenterX);

  const node = ctx.nodeById.get(nodeId);
  if (!node) return;

  if (node.data?.type === 'true-false') {
    const { trueChildId, falseChildId } = getBranchChildren(
      nodeId,
      ctx.outgoingBySource
    );
    const trueUnits = trueChildId
      ? ctx.subtreeUnitsByNode.get(trueChildId) || 1
      : 1;
    const falseUnits = falseChildId
      ? ctx.subtreeUnitsByNode.get(falseChildId) || 1
      : 1;

    const trueWidthPx = trueUnits * SUBTREE_UNIT_WIDTH;
    const falseWidthPx = falseUnits * SUBTREE_UNIT_WIDTH;
    const gapPx = DECISION_BRANCH_GAP_UNITS * SUBTREE_UNIT_WIDTH;
    const totalBranchesWidth = trueWidthPx + gapPx + falseWidthPx;
    const branchStart = nodeCenterX - totalBranchesWidth / 2;

    if (trueChildId) {
      positionSubtree(trueChildId, branchStart, ctx);
    }
    if (falseChildId && falseChildId !== trueChildId) {
      positionSubtree(falseChildId, branchStart + trueWidthPx + gapPx, ctx);
    }
    return;
  }

  const children = ctx.childrenByParent.get(nodeId) || [];
  if (children.length === 0) return;

  const totalChildrenWidth = children.reduce((sum, childId, index) => {
    const childUnits = ctx.subtreeUnitsByNode.get(childId) || 1;
    const childWidthPx = childUnits * SUBTREE_UNIT_WIDTH;
    const gapPx =
      index > 0 ? SUBTREE_SIBLING_GAP_UNITS * SUBTREE_UNIT_WIDTH : 0;
    return sum + childWidthPx + gapPx;
  }, 0);

  let currentLeft = nodeCenterX - totalChildrenWidth / 2;
  for (const childId of children) {
    positionSubtree(childId, currentLeft, ctx);
    const childUnits = ctx.subtreeUnitsByNode.get(childId) || 1;
    currentLeft += childUnits * SUBTREE_UNIT_WIDTH;
    currentLeft += SUBTREE_SIBLING_GAP_UNITS * SUBTREE_UNIT_WIDTH;
  }
}

function shiftSubtree(
  nodeId: string,
  deltaX: number,
  ctx: LayoutGraphContext
): void {
  const stack = [nodeId];
  const visited = new Set<string>();
  while (stack.length) {
    const current = stack.pop()!;
    if (visited.has(current)) continue;
    visited.add(current);
    const currentX = ctx.xCenterByNode.get(current) || 0;
    ctx.xCenterByNode.set(current, currentX + deltaX);
    const children = ctx.childrenByParent.get(current) || [];
    for (const childId of children) stack.push(childId);
  }
}

function alignMergeNodesToIncomingMidpoint(ctx: LayoutGraphContext): void {
  for (const [nodeId, node] of Array.from(ctx.nodeById.entries())) {
    if (node.data?.type !== 'merge') continue;
    const incoming = ctx.incomingByTarget.get(nodeId) || [];
    if (incoming.length < 2) continue;
    const sourceCenters = incoming
      .map((edge) => ctx.xCenterByNode.get(edge.source))
      .filter((v): v is number => typeof v === 'number');
    if (sourceCenters.length < 2) continue;
    const desiredCenter =
      (Math.min(...sourceCenters) + Math.max(...sourceCenters)) / 2;
    const currentCenter = ctx.xCenterByNode.get(nodeId) || 0;
    const delta = desiredCenter - currentCenter;
    if (Math.abs(delta) < 1) continue;
    shiftSubtree(nodeId, delta, ctx);
  }
}

function alignExitNodeToIncomingMidpoint(ctx: LayoutGraphContext): void {
  const exitNode = Array.from(ctx.nodeById.values()).find(
    (node) => node.data?.type === 'exit'
  );
  if (!exitNode) return;

  const triggerNode = Array.from(ctx.nodeById.values()).find(
    (node) => node.data?.type === 'trigger'
  );
  const triggerCenter = triggerNode
    ? ctx.xCenterByNode.get(triggerNode.id)
    : undefined;

  const incoming = ctx.incomingByTarget.get(exitNode.id) || [];
  if (incoming.length === 0) return;
  const sourceCenters = incoming
    .map((edge) => ctx.xCenterByNode.get(edge.source))
    .filter((v): v is number => typeof v === 'number');
  if (sourceCenters.length === 0) return;

  const minCenter = Math.min(...sourceCenters);
  const maxCenter = Math.max(...sourceCenters);
  const incomingCenter =
    minCenter + (maxCenter - minCenter) * EXIT_CENTER_WEIGHT;
  const desiredCenter =
    typeof triggerCenter === 'number' ? triggerCenter : incomingCenter;
  const currentCenter = ctx.xCenterByNode.get(exitNode.id) || 0;
  const delta = desiredCenter - currentCenter;
  if (Math.abs(delta) < 1) return;
  shiftSubtree(exitNode.id, delta, ctx);
}

function alignMergeAndExitCenters(ctx: LayoutGraphContext): void {
  alignMergeNodesToIncomingMidpoint(ctx);
  alignExitNodeToIncomingMidpoint(ctx);
}

function reserveInterval(
  intervals: Array<[number, number]>,
  left: number,
  right: number
): void {
  intervals.push([left, right]);
  intervals.sort((a, b) => a[0] - b[0]);
}

function findNonOverlappingLeft(
  desiredLeft: number,
  width: number,
  intervals: Array<[number, number]>
): number {
  let candidate = desiredLeft;
  const MIN_GAP = ROOT_GAP_PX;
  for (const [left, right] of intervals) {
    const overlaps =
      candidate < right + MIN_GAP && candidate + width + MIN_GAP > left;
    if (overlaps) {
      candidate = right + MIN_GAP;
    }
  }
  return candidate;
}

/** Recursive subtree positioning (X) with optional affected-root recompute. */
function positionSubtreesRecursively(
  nodes: Node[],
  edges: Edge[],
  hint?: WorkflowLayoutHint
): Node[] {
  const ctx = createLayoutGraphContext(nodes, edges);
  const roots = nodes
    .filter((node) => !ctx.primaryParentByNode.has(node.id))
    .sort((a, b) => {
      const ax = ctx.previousCenterByNode.get(a.id) ?? 0;
      const bx = ctx.previousCenterByNode.get(b.id) ?? 0;
      if (ax !== bx) return ax - bx;
      return a.id.localeCompare(b.id);
    });

  computeSubtreeWidths(
    nodes.map((node) => node.id),
    ctx
  );
  const impactedRootIds = deriveImpactedRootIds(roots, edges, ctx, hint);
  const occupiedIntervals: Array<[number, number]> = [];

  roots.forEach((root) => {
    if (impactedRootIds.has(root.id)) return;
    const subtreeIds = getSubtreeNodeIds(root.id, ctx);
    subtreeIds.forEach((nodeId) => {
      const prevCenter = ctx.previousCenterByNode.get(nodeId);
      if (typeof prevCenter === 'number') {
        ctx.xCenterByNode.set(nodeId, prevCenter);
      }
    });
    const rootCenter = ctx.previousCenterByNode.get(root.id) ?? 0;
    const rootWidthPx =
      (ctx.subtreeUnitsByNode.get(root.id) || 1) * SUBTREE_UNIT_WIDTH;
    reserveInterval(
      occupiedIntervals,
      rootCenter - rootWidthPx / 2,
      rootCenter + rootWidthPx / 2
    );
  });

  let nextLeft = 0;
  for (const root of roots) {
    if (!impactedRootIds.has(root.id)) continue;
    const rootUnits = ctx.subtreeUnitsByNode.get(root.id) || 1;
    const rootWidthPx = rootUnits * SUBTREE_UNIT_WIDTH;
    const desiredCenter =
      ctx.previousCenterByNode.get(root.id) ?? rootWidthPx / 2;
    const desiredLeft = desiredCenter - rootWidthPx / 2;
    const rootLeft = findNonOverlappingLeft(
      Math.max(nextLeft, desiredLeft),
      rootWidthPx,
      occupiedIntervals
    );
    positionSubtree(root.id, rootLeft, ctx);
    reserveInterval(occupiedIntervals, rootLeft, rootLeft + rootWidthPx);
    nextLeft = rootLeft + rootWidthPx + ROOT_GAP_PX;
  }

  alignMergeAndExitCenters(ctx);

  return nodes.map((node) => {
    const width = ctx.nodeWidthById.get(node.id) || DAGRE_NODE_WIDTH;
    const centerX = ctx.xCenterByNode.get(node.id);
    if (typeof centerX !== 'number') return node;
    return {
      ...node,
      position: {
        x: centerX - width / 2,
        y: node.position.y,
      },
    };
  });
}

function computeVerticalRanksWithDagre(
  nodes: Node[],
  edges: Edge[],
  direction: 'TB' | 'LR'
): { nodes: Node[]; sortedEdges: Edge[] } {
  const sortedEdges = sortEdgesForDagre(nodes, edges);
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: direction,
    ranksep: 110,
    nodesep: 90,
  });

  nodes.forEach((node) => {
    const height = estimateNodeHeight(node);
    dagreGraph.setNode(node.id, { width: DAGRE_NODE_WIDTH, height });
  });

  sortedEdges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const yRankedNodes = nodes.map((node) => {
    const nodePosition = dagreGraph.node(node.id);
    const height = estimateNodeHeight(node);
    const previousX =
      typeof node.position?.x === 'number' ? node.position.x : 0;
    const width = getNodeWidth(node);

    return {
      ...node,
      data: node.data ? { ...node.data } : node.data,
      position: {
        x: previousX,
        y: nodePosition.y - height / 2,
      },
      measured:
        node.measured && typeof node.measured.width === 'number'
          ? { ...node.measured, width }
          : node.measured
            ? { ...node.measured }
            : undefined,
    } as Node;
  });

  return {
    nodes: alignImmediateDecisionChildrenTop(yRankedNodes, sortedEdges),
    sortedEdges,
  };
}

function hashString(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) {
    h = (h << 5) - h + input.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function assignEdgeRoutingHints(edges: Edge[]): Edge[] {
  return edges.map((edge) => {
    const hint =
      edge.targetHandle === 'merge-true' ||
      edge.targetHandle === 'merge-false' ||
      edge.sourceHandle === 'true' ||
      edge.sourceHandle === 'false'
        ? { stepPosition: 0, laneOffset: 0 }
        : (() => {
            const lane = (hashString(edge.id) % 3) - 1; // -1, 0, 1
            return {
              stepPosition: 0.5 + lane * 0.06,
              laneOffset: lane * 10,
            };
          })();
    return {
      ...edge,
      data: {
        ...(edge.data || {}),
        routing: hint,
      },
    };
  });
}

/** Two branches into one node: attach to separate target handles so edges do not cross. */
export function assignMergeTargetHandles(nodes: Node[], edges: Edge[]): Edge[] {
  const typeById: Record<string, string | undefined> = {};
  for (const n of nodes) {
    typeById[n.id] = n.data?.type as string | undefined;
  }

  return edges.map((edge) => {
    const { targetHandle, ...rest } = edge as Edge & { targetHandle?: string };

    const targetType = typeById[edge.target];

    if (
      typeById[edge.source] !== 'true-false' ||
      targetType === 'merge' ||
      targetType === 'exit'
    ) {
      if (targetHandle === 'merge-true' || targetHandle === 'merge-false') {
        return rest as Edge;
      }
      return edge;
    }

    const partner = edges.find(
      (e) =>
        e.id !== edge.id &&
        e.source === edge.source &&
        e.target === edge.target &&
        ((e.sourceHandle === 'true' && edge.sourceHandle === 'false') ||
          (e.sourceHandle === 'false' && edge.sourceHandle === 'true'))
    );

    if (!partner) {
      if (targetHandle === 'merge-true' || targetHandle === 'merge-false') {
        return rest as Edge;
      }
      return edge;
    }

    if (edge.sourceHandle === 'true') {
      return { ...edge, targetHandle: 'merge-true' };
    }
    if (edge.sourceHandle === 'false') {
      return { ...edge, targetHandle: 'merge-false' };
    }
    return edge;
  });
}

/** True/false merge into one downstream node — show split target handles on that node (not on Exit). */
export function nodeHasMergeIncomingBranches(
  nodeId: string,
  nodes: Node[],
  edges: Edge[]
): boolean {
  if (nodes.find((n) => n.id === nodeId)?.data?.type === 'exit') {
    return false;
  }
  const incoming = edges.filter((e) => e.target === nodeId);
  const bySource = new Map<string, Edge[]>();
  for (const e of incoming) {
    const list = bySource.get(e.source) || [];
    list.push(e);
    bySource.set(e.source, list);
  }
  return Array.from(bySource.entries()).some(([sourceId, list]) => {
    const t = nodes.find((n) => n.id === sourceId)?.data?.type;
    if (t !== 'true-false') return false;
    const hs = new Set(
      list.map((e: Edge) => e.sourceHandle).filter(Boolean) as string[]
    );
    return hs.has('true') && hs.has('false');
  });
}

export function getLayoutedElements(
  nodes: Node[],
  edges: Edge[],
  direction: 'TB' | 'LR' = 'TB',
  layoutHint?: WorkflowLayoutHint
): { nodes: Node[]; edges: Edge[] } {
  const { nodes: verticalRankedNodes, sortedEdges } =
    computeVerticalRanksWithDagre(nodes, edges, direction);
  const layoutedNodes = positionSubtreesRecursively(
    verticalRankedNodes,
    sortedEdges,
    layoutHint
  );

  let layoutedEdges = sortedEdges.map((edge) => ({ ...edge }));
  layoutedEdges = assignMergeTargetHandles(layoutedNodes, layoutedEdges);
  layoutedEdges = assignEdgeRoutingHints(layoutedEdges);

  return { nodes: layoutedNodes, edges: layoutedEdges };
}
